import { estVisiteRealisee, lireCommentaireAction } from "./planningCrmUtils";
import { syntheseCaInterclubV2 } from "./interclubDirectSync";

export const QUALITE_FINANCIERE = "NON_CERTIFIE";

export const arrondiMonetaire = (valeur) => Math.round((Number(valeur || 0) + Number.EPSILON) * 100) / 100;
export function arrondirPrimeTotale(montant, configuration = {}) {
  const brut = arrondiMonetaire(montant);
  if (configuration.arrondiPrimeTotale !== true) return brut;
  const pas = Number(configuration.pasArrondi) === 10 ? 10 : 1;
  const quotient = brut / pas;
  const arrondi = configuration.sensArrondi === "EXCES"
    ? (Math.abs(quotient - Math.round(quotient)) < 1e-9 ? Math.round(quotient) : Math.ceil(quotient)) * pas
    : Math.round(quotient) * pas;
  return arrondiMonetaire(arrondi);
}

const iso = (date) => {
  const valeur = new Date(date);
  if (Number.isNaN(valeur.valueOf())) return "";
  return `${valeur.getFullYear()}-${String(valeur.getMonth() + 1).padStart(2, "0")}-${String(valeur.getDate()).padStart(2, "0")}`;
};

const dans = (date, debut, fin) => {
  const valeur = String(date || "").slice(0, 10);
  return Boolean(valeur && valeur >= debut && valeur <= fin);
};

export function periodesPointCommercial(annee, mois, debutExercice = "04-01", maintenant = new Date()) {
  const anneeMois = Number(annee);
  const numeroMois = Number(mois);
  const debutMois = new Date(anneeMois, numeroMois - 1, 1, 12);
  const finMois = new Date(anneeMois, numeroMois, 0, 12);
  const [moisExercice, jourExercice] = String(debutExercice || "04-01").split("-").map(Number);
  const anneeExercice = numeroMois >= moisExercice ? anneeMois : anneeMois - 1;
  const debutCumul = new Date(anneeExercice, moisExercice - 1, jourExercice || 1, 12);
  const finAujourdHui = new Date(maintenant.getFullYear(), maintenant.getMonth(), maintenant.getDate(), 23, 59, 59);
  return {
    mois: { debut: iso(debutMois), fin: iso(finMois) },
    cumul: { debut: iso(debutCumul), fin: iso(finMois) },
    dateArrete: iso(finMois),
    nonCloturee: finMois > finAujourdHui,
    libelle: debutMois.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }).toUpperCase()
  };
}

export function syntheseFinancePointCommercial(documents = [], codesClients = new Set(), options = {}) {
  const periodes = options.periodes || periodesPointCommercial(options.annee, options.mois);
  const finance = syntheseCaInterclubV2(documents, codesClients, {
    commercialCommande: options.commercialCommande || "",
    codesClientsConnus: options.codesClientsConnus || codesClients,
    inclureNonRapproches: options.inclureNonRapproches !== false,
    debutMois: periodes.mois.debut,
    finMois: periodes.mois.fin,
    debutCumul: periodes.cumul.debut,
    finCumul: periodes.cumul.fin,
  });
  return { ...finance, periodes };
}

export function visitesPointCommercial(actions = [], periodes, commercial = "") {
  const cible = String(commercial || "").trim().toLocaleLowerCase("fr-FR");
  const visites = actions.filter((action) => {
    if (!String(action.type || "").toLocaleLowerCase("fr-FR").includes("visite") && action.type !== "Visite / RDV") return false;
    return !cible || cible === "tous" || cible === "toute l'équipe" || String(action.commercial || "").trim().toLocaleLowerCase("fr-FR") === cible;
  }).map((action) => {
    const meta = lireCommentaireAction(action.commentaire).meta;
    const qualification = meta.qualification || null;
    return { ...action, qualification, dateQualification: qualification?.date_qualification || "" };
  });
  const compter = (periode) => ({
    realisees: visites.filter((item) => estVisiteRealisee(item) && dans(item.date_rdv, periode.debut, periode.fin)).length,
    qualifiees: visites.filter((item) => dans(item.dateQualification, periode.debut, periode.fin)).length,
    details: visites.filter((item) => estVisiteRealisee(item) && dans(item.date_rdv, periode.debut, periode.fin))
  });
  return { mois: compter(periodes.mois), cumul: compter(periodes.cumul) };
}

export function boutiquePointCommercial(source, periodes, commercial = "") {
  const campagnes = source?.campagnes || [];
  const commandes = source?.commandes || [];
  const cible = String(commercial || "").trim().toLocaleLowerCase("fr-FR");
  const campagnesCible = campagnes.filter((campagne) => !cible || cible === "tous" || cible === "toute l'équipe" || String(campagne.commercial_nom || "").trim().toLocaleLowerCase("fr-FR") === cible);
  const ids = new Set(campagnesCible.map((campagne) => String(campagne.id)));
  const compter = (periode) => {
    const actives = campagnesCible.filter((campagne) => String(campagne.ouverture_at || "").slice(0, 10) <= periode.fin && String(campagne.fermeture_at || "9999").slice(0, 10) >= periode.debut);
    const commandesPeriode = commandes.filter((commande) => ids.has(String(commande.campagne_id)) && commande.statut !== "ANNULEE" && dans(commande.created_at, periode.debut, periode.fin));
    return { campagnes: new Set(actives.map((item) => item.id)).size, commandes: commandesPeriode.length, ca: commandesPeriode.reduce((somme, item) => somme + Number(item.total_ttc || 0), 0) };
  };
  return { mois: compter(periodes.mois), cumul: compter(periodes.cumul) };
}

export function contratsPointCommercial(contrats = [], periodes, commercial = "") {
  const cible = String(commercial || "").trim().toLocaleLowerCase("fr-FR");
  const liste = contrats.filter((contrat) => !cible || cible === "tous" || cible === "toute l'équipe" || String(contrat.commercial_nom || contrat.commercial || "").trim().toLocaleLowerCase("fr-FR") === cible);
  const evenement = (contrat, nature) => String(contrat.evenement || contrat.statut_renouvellement || contrat.statut || "").toUpperCase().includes(nature);
  const dateEvenement = (contrat) => contrat.date_evenement || contrat.updated_at || contrat.date_debut || contrat.date_fin;
  const compter = (periode) => ({
    aRenouveler: liste.filter((item) => dans(item.date_fin, periode.debut, periode.fin)).length,
    renouveles: liste.filter((item) => evenement(item, "RENOUVEL") && dans(dateEvenement(item), periode.debut, periode.fin)).length,
    recrutes: liste.filter((item) => (evenement(item, "RECRUT") || evenement(item, "NOUVEAU")) && dans(dateEvenement(item), periode.debut, periode.fin)).length,
    perdus: liste.filter((item) => (evenement(item, "PERDU") || evenement(item, "RESIL")) && dans(dateEvenement(item), periode.debut, periode.fin)).length
  });
  const arrete = new Date(`${periodes.dateArrete || periodes.mois.fin}T12:00:00`);
  const echeances = liste.filter((item) => {
    const fin = new Date(`${String(item.date_fin || "").slice(0, 10)}T12:00:00`);
    const jours = Math.ceil((fin - arrete) / 86400000);
    return jours >= 0;
  }).map((item) => {
    const jours = Math.ceil((new Date(`${String(item.date_fin).slice(0, 10)}T12:00:00`) - arrete) / 86400000);
    return { ...item, joursRestants: jours, tranche: jours <= 31 ? "≤1 mois" : jours <= 92 ? "1–3 mois" : jours <= 183 ? "3–6 mois" : ">6 mois" };
  });
  return { mois: compter(periodes.mois), cumul: compter(periodes.cumul), echeances, tranches: { "≤1 mois": echeances.filter((x) => x.tranche === "≤1 mois").length, "1–3 mois": echeances.filter((x) => x.tranche === "1–3 mois").length, "3–6 mois": echeances.filter((x) => x.tranche === "3–6 mois").length, ">6 mois": echeances.filter((x) => x.tranche === ">6 mois").length } };
}

export function meilleurPalierBareme(realise = 0, objectif = 0, bareme = []) {
  const cible = Number(objectif || 0);
  const valeur = Number(realise || 0);
  const atteinte = cible > 0 ? (valeur / cible) * 100 : 0;
  const paliers = (Array.isArray(bareme) ? bareme : [])
    .map((palier) => ({ seuil: Number(palier?.seuil || 0), valeur: Number(palier?.valeur || 0) }))
    .filter((palier) => Number.isFinite(palier.seuil) && palier.seuil >= 0)
    .sort((a, b) => a.seuil - b.seuil);
  const applicable = paliers.filter((palier) => palier.seuil <= atteinte).at(-1) || null;
  return { objectif: cible, realise: valeur, atteinte, seuil: applicable?.seuil ?? null, prime: applicable?.valeur || 0 };
}

export function encaissementsPointCommercial(documents = [], clubs = [], periodes, commercial = "") {
  const commandes = new Map(documents.filter((item) => item.type_document === "COMMANDE").map((item) => [String(item.donnees?.numero_commande || item.identifiant_source || ""), item]));
  const clubsParCode = new Map(clubs.map((club) => [String(club.numero_compte_interclub || club.code || ""), club]));
  const cible = String(commercial || "").trim().toLocaleLowerCase("fr-FR");
  // Un encaissement appartient au mois de sa date effective de paiement/imputation.
  // La date de facture ne doit jamais piloter cette sélection mensuelle.
  return documents.filter((item) => item.type_document === "PAIEMENT" && dans(item.donnees?.date_paiement, periodes.mois.debut, periodes.mois.fin)).map((item) => {
    const numeroCommande = String(item.donnees?.numero_commande || "");
    const commande = commandes.get(numeroCommande);
    const commercialCommande = String(commande?.donnees?.commercial_commande || "");
    const club = clubsParCode.get(String(item.donnees?.code_client || ""));
    return { club: club?.nom || item.donnees?.nom_client || item.donnees?.code_client || "—", commande: numeroCommande, facture: item.donnees?.numero_facture || "", dateFacture: item.donnees?.date_facturation || "", dateEncaissement: item.donnees?.date_paiement || "", montantEncaisse: Number(item.donnees?.montant_impute ?? item.donnees?.montant ?? 0), commercial: commercialCommande };
  }).filter((item) => !cible || cible === "tous" || cible === "toute l'équipe" || item.commercial.trim().toLocaleLowerCase("fr-FR") === cible);
}

export function objectifsCumulesExercice(objectifs = [], debutExercice = "", finCumul = "") {
  const debut = String(debutExercice || "").slice(0, 7);
  const fin = String(finCumul || "").slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(debut) || !/^\d{4}-\d{2}$/.test(fin) || debut > fin) {
    return { complet: false, total: 0, objectifs: [], moisManquants: [] };
  }
  const parMois = new Map();
  (Array.isArray(objectifs) ? objectifs : []).forEach((objectif) => {
    const mois = String(objectif?.mois || objectif?.periode || "").slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(mois)) return;
    const valeur = Number(objectif?.valeur ?? objectif?.valeur_objectif);
    if (!Number.isFinite(valeur)) return;
    parMois.set(mois, (parMois.get(mois) || 0) + valeur);
  });
  const attendus = [];
  const curseur = new Date(`${debut}-01T12:00:00`);
  const dernier = new Date(`${fin}-01T12:00:00`);
  while (curseur <= dernier) {
    attendus.push(`${curseur.getFullYear()}-${String(curseur.getMonth() + 1).padStart(2, "0")}`);
    curseur.setMonth(curseur.getMonth() + 1);
  }
  const moisManquants = attendus.filter((mois) => !parMois.has(mois));
  const liste = attendus.filter((mois) => parMois.has(mois)).map((mois) => ({ mois, valeur: parMois.get(mois) }));
  return { complet: moisManquants.length === 0, total: arrondiMonetaire(liste.reduce((somme, item) => somme + item.valeur, 0)), objectifs: liste, moisManquants };
}

export function calculerPrimeBrouillon({ indicateurs, regle = {}, primeSurperformanceDejaValidee = 0, qualiteFinanciere = QUALITE_FINANCIERE }) {
  const actif = regle.leviers || {};
  const primeMensuelleCalculable = indicateurs?.caEncaisseMoisCertifie === true;
  const surperformanceCalculable = indicateurs?.objectifCaCumulComplet === true && indicateurs?.caEncaisseCumulCertifie === true;
  const ca = actif.ca === false || !primeMensuelleCalculable ? 0 : arrondiMonetaire(Number(indicateurs?.caEncaisseMois || 0) * Number(regle.tauxCaEncaisse || 0) / 100);
  const depassement = Math.max(0, Number(indicateurs?.caEncaisseCumul || 0) - Number(indicateurs?.objectifCaCumul || 0));
  const primeTheoriqueCumulee = actif.ca === false || !surperformanceCalculable ? 0 : arrondiMonetaire(depassement * Number(regle.tauxSurperformance || 0) / 100);
  const dejaValidee = Math.max(0, Number(primeSurperformanceDejaValidee || 0));
  const complementSurperformance = surperformanceCalculable ? arrondiMonetaire(Math.max(0, primeTheoriqueCumulee - dejaValidee)) : 0;
  const surversementTheorique = surperformanceCalculable ? arrondiMonetaire(Math.max(0, dejaValidee - primeTheoriqueCumulee)) : 0;
  const baseSurprime = depassement;
  const surprime = complementSurperformance;
  const visitesRealisees = Number(indicateurs?.visitesRealiseesMois ?? indicateurs?.visitesQualifieesMois ?? 0);
  const visitesDetail = meilleurPalierBareme(visitesRealisees, regle.objectifVisites, regle.baremeVisites);
  const visitesLegacy = visitesRealisees >= Number(regle.objectifVisites || 0) ? Number(regle.primeVisites || 0) : 0;
  const visites = actif.visites ? (Array.isArray(regle.baremeVisites) ? visitesDetail.prime : visitesLegacy) : 0;
  const criteresBoutique = regle.criteresBoutique || { boutiques: true, commandes: true, ca: true };
  const boutiqueBoutiques = meilleurPalierBareme(indicateurs?.boutiqueCampagnesMois, regle.objectifCampagnes, regle.baremeBoutiques);
  const boutiqueCommandes = meilleurPalierBareme(indicateurs?.boutiqueCommandesMois, regle.objectifCommandes, regle.baremeCommandes);
  const boutiqueCa = Number(indicateurs?.boutiqueCaMois || 0) * Number(regle.tauxBoutique || 0) / 100;
  const boutique = actif.boutique ? (
    (criteresBoutique.boutiques === false ? 0 : boutiqueBoutiques.prime) +
    (criteresBoutique.commandes === false ? 0 : boutiqueCommandes.prime) +
    (criteresBoutique.ca === false ? 0 : boutiqueCa)
  ) : 0;
  const contratsRenouvellement = meilleurPalierBareme(indicateurs?.contratsRenouvelesMois, indicateurs?.contratsARenouvelerMois, regle.baremeRenouvellement);
  const contratsRecrutement = meilleurPalierBareme(indicateurs?.clubsRecrutesMois, regle.objectifRecrutement, regle.baremeRecrutement);
  const contrats = actif.contrats ? contratsRenouvellement.prime + contratsRecrutement.prime : 0;
  const primeFinanciere = arrondiMonetaire(ca + complementSurperformance);
  const primeTotaleBrute = arrondiMonetaire(primeFinanciere + visites + boutique + contrats);
  const primeTotaleAVerser = arrondirPrimeTotale(primeTotaleBrute, regle);
  const primeTotaleMois = primeTotaleAVerser;
  const total = primeTotaleAVerser;
  const validationAutorisee = qualiteFinanciere === "CERTIFIE" && primeMensuelleCalculable && surperformanceCalculable;
  return {
    ca, primeMensuelle: ca, surprime, visites, boutique, contrats, depassement, baseSurprime, total,
    primeTheoriqueCumulee, primeSurperformanceDejaValidee: dejaValidee,
    complementSurperformance, primeFinanciere, primeTotaleBrute, primeTotaleAVerser, primeTotaleMois, surversementTheorique,
    arrondiPrimeTotale: regle.arrondiPrimeTotale === true, pasArrondi: Number(regle.pasArrondi) === 10 ? 10 : 1,
    sensArrondi: regle.sensArrondi === "EXCES" ? "EXCES" : "PLUS_PROCHE",
    alerteSurversement: surversementTheorique > 0 ? "SURVERSEMENT THÉORIQUE À CONTRÔLER" : null,
    primeMensuelleCalculable, surperformanceCalculable,
    statutPrimeMensuelle: primeMensuelleCalculable ? "CALCULABLE" : "CA ENCAISSÉ DU MOIS NON CERTIFIÉ",
    statutSurperformance: indicateurs?.objectifCaCumulComplet !== true
      ? "NON CALCULABLE / OBJECTIF INCOMPLET"
      : indicateurs?.caEncaisseCumulCertifie !== true ? "CA ENCAISSÉ CUMULÉ NON CERTIFIÉ / PRIME SURPERFORMANCE BLOQUÉE" : "CALCULABLE",
    visitesDetail: { ...visitesDetail, prime: visites },
    boutiqueDetail: { boutiques: boutiqueBoutiques, commandes: boutiqueCommandes, ca: { base: Number(indicateurs?.boutiqueCaMois || 0), taux: Number(regle.tauxBoutique || 0), prime: actif.boutique && criteresBoutique.ca !== false ? boutiqueCa : 0 } },
    contratsDetail: { renouvellement: contratsRenouvellement, recrutement: contratsRecrutement },
    qualiteFinanciere, validationAutorisee
  };
}

export function snapshotPrime({ contexte, regle, calcul, validateur = null, dateValidation = null }) {
  const i = contexte?.indicateurs || {};
  return {
    commercial: contexte?.commercial || "", mois_analyse: `${contexte?.annee}-${String(contexte?.mois || "").padStart(2, "0")}`,
    debut_mois: contexte?.periodes?.mois?.debut, fin_mois: contexte?.periodes?.mois?.fin,
    debut_exercice: contexte?.periodes?.cumul?.debut, fin_cumul: contexte?.periodes?.cumul?.fin,
    ca_facture: i.caFactureMois || 0, ca_encaisse: i.caEncaisseMois || 0, ca_cumule: i.caEncaisseCumul || 0,
    objectifs: { mois: i.objectifCaMois || 0, cumul: i.objectifCaCumul || 0, mensuels_utilises: i.objectifsCaMensuelsExercice || [], mois_manquants: i.moisObjectifsManquants || [], visites: regle?.objectifVisites || 0 },
    taux_appliques: { ca: regle?.tauxCaEncaisse || 0, surprime: regle?.tauxSurperformance || 0, boutique: regle?.tauxBoutique || 0 },
    mode_surprime: "CUMUL_EXERCICE_SANS_CASCADE", depassement_primable: calcul?.depassement || 0,
    prime_mensuelle: calcul?.primeMensuelle || 0,
    prime_surperformance_theorique_cumulee: calcul?.primeTheoriqueCumulee || 0,
    prime_surperformance_deja_validee: calcul?.primeSurperformanceDejaValidee || 0,
    complement_surperformance_mois: calcul?.complementSurperformance || 0,
    surversement_theorique: calcul?.surversementTheorique || 0,
    alerte_surversement: calcul?.alerteSurversement || null,
    prime_totale_brute: calcul?.primeTotaleBrute ?? calcul?.primeTotaleMois ?? 0,
    arrondi_prime_totale: calcul?.arrondiPrimeTotale === true,
    pas_arrondi: calcul?.pasArrondi || 1,
    sens_arrondi: calcul?.sensArrondi || "PLUS_PROCHE",
    prime_totale_a_verser: calcul?.primeTotaleAVerser ?? calcul?.primeTotaleMois ?? 0,
    prime_totale_mois: calcul?.primeTotaleAVerser ?? calcul?.primeTotaleMois ?? 0,
    dette_a_date: contexte?.dette || null,
    visites_realisees: i.visitesRealiseesMois ?? i.visitesQualifieesMois ?? 0,
    visites_qualifiees: i.visitesQualifieesMois || 0,
    boutiqueclub: contexte?.boutique || {}, contrats: contexte?.contrats || {},
    composantes: { ca: calcul?.ca || 0, surprime: calcul?.surprime || 0, visites: calcul?.visites || 0, boutique: calcul?.boutique || 0, contrats: calcul?.contrats || 0 },
    total: calcul?.total || 0, validateur, date_validation: dateValidation
  };
}

export function contexteActionPointCommercial({ commercial, mois, club, motif, createur, type, dateEcheance }) {
  return {
    type: type || "Tâche",
    commercial,
    club_id: club?.id || club?.identifiant || null,
    clubCode: club?.code || "",
    clubNom: club?.nom || "",
    date_rdv: dateEcheance,
    origine: "POINT COMMERCIAL",
    mois_analyse: mois,
    motif,
    createur
  };
}
